import { useEffect, useRef, useState, type CSSProperties } from "react";
import { ArrowUpRight, Check, CheckCheck, Compass, LoaderCircle, Minus, Sparkles } from "lucide-react";
import { chunkzDecisions, type ChunkzDecision } from "../shared/chunkz";
import "./decision.css";

const choices = {
  Yes: { caption: "Looks right", icon: Check },
  Maybe: { caption: "Let’s explore", icon: Compass },
  No: { caption: "Not for this", icon: Minus },
};

export interface DecisionPanelProps {
  title: string;
  decision: ChunkzDecision;
  busy: boolean;
  pending: ChunkzDecision | null;
  feedback: { decision: ChunkzDecision; sequence: number } | null;
  buyer: boolean;
  updated?: string;
  onChoose: (decision: ChunkzDecision) => void;
}

export function DecisionPanel({ title, decision, busy, pending, feedback, buyer, updated, onChoose }: DecisionPanelProps) {
  const panel = useRef<HTMLElement>(null);
  const [entered, setEntered] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  useEffect(() => {
    const element = panel.current;
    if (!element) return;
    if (!("IntersectionObserver" in window)) { setEntered(true); return; }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setEntered(true); observer.disconnect(); }
    }, { threshold: .2 });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    setCelebrating(Boolean(feedback));
    if (!feedback) return;
    const timer = window.setTimeout(() => setCelebrating(false), 2200);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  return <section ref={panel} className={`decision-panel${entered ? " has-entered" : ""}${celebrating ? " is-celebrating" : ""}`} data-choice={decision.toLowerCase() || "none"} aria-label={`Your decision on ${title}`}
    onPointerMove={event => {
      if (event.pointerType !== "mouse") return;
      const bounds = event.currentTarget.getBoundingClientRect();
      event.currentTarget.style.setProperty("--spot-x", `${event.clientX - bounds.left}px`);
      event.currentTarget.style.setProperty("--spot-y", `${event.clientY - bounds.top}px`);
    }}>
    <div className="decision-aura" aria-hidden="true" />
    <div className="decision-heading"><div><span className="decision-eyebrow">Your next move</span><h3>Does this fit?</h3></div><span className="decision-emblem" aria-hidden="true"><Sparkles size={23} /></span></div>
    <p className="decision-intro">{buyer ? "A first impression. A step closer." : "Buyer decision · shared with everyone"}</p>
    <div className="decision-options" role="group" aria-label="Choose Yes, Maybe or No" aria-busy={pending !== null}>
      {chunkzDecisions.map(value => {
        const Icon = choices[value].icon;
        const selected = decision === value;
        return <button key={value} type="button" className={`decision-option option-${value.toLowerCase()}${selected ? " is-selected" : ""}`} aria-label={value} aria-pressed={selected} disabled={busy} onClick={() => onChoose(selected ? "" : value)}>
          <span className="decision-option-icon" aria-hidden="true">{pending === value || (pending === "" && selected) ? <LoaderCircle size={20} className="spin" /> : <Icon size={20} />}</span>
          <span className="decision-option-label">{value}</span><span className="decision-option-caption" aria-hidden="true">{choices[value].caption}</span>
          <CheckCheck className="decision-selected-mark" size={14} aria-hidden="true" />
        </button>;
      })}
    </div>
    <p className="decision-change-hint">You can change your mind anytime. Just choose another option.</p>
    <div className="decision-footer" aria-hidden="true" key={`message-${feedback?.sequence || 0}`}>
      {pending !== null ? <><LoaderCircle className="spin" size={14} /> Saving your choice…</> : celebrating && feedback ? <><CheckCheck size={15} /> {feedback.decision ? `${feedback.decision} — saved. You’re all set.` : "Choice cleared. A fresh look."}</> : decision ? <><CheckCheck size={15} /> {decision} selected <span>Click again to clear</span></> : <><ArrowUpRight size={15} /> Pick a direction. We’ll take it from here.</>}
    </div>
    {updated && <small className="decision-updated">{updated}</small>}
    {celebrating && <div key={`flourish-${feedback?.sequence}`} className="decision-flourish" aria-hidden="true"><span className="decision-ring" />{Array.from({ length: 9 }, (_, i) => <i key={i} style={{ "--angle": `${i * 40}deg`, "--travel": `${50 + (i % 3) * 20}px`, "--delay": `${(i % 3) * 35}ms` } as CSSProperties} />)}</div>}
  </section>;
}
